<script lang="ts">
    import ReviewsRatingStars from "@apps/reviews/components_v2/ReviewsRatingStars/ReviewsRatingStars.svelte";
    import ReviewsTotalRate from "@apps/reviews/components_v2/ReviewsTotalRate/ReviewsTotalRate.svelte";
    import ReviewsTheme from "@apps/reviews/components/ReviewsTheme/ReviewsTheme.svelte";
    import ReviewsStorefrontValuesService from "@apps/reviews/services/ReviewsStorefrontValuesService";
    import Tooltip from "@components_v2/Tooltip/Tooltip.svelte";
    import Text from "@components_v2/Typography/Text.svelte";
    import { ProductIdManagerInterface } from "@lib/ProductIdManager/ProductIdManagerInterface";
    import { filter, map, switchMap, tap } from "rxjs";
    import { onMount } from "svelte";

    import BaseSkeleton from "@components/skeletons/BaseSkeleton.svelte";

    import ListingProductInfoQuery from "../queries/ListingProductInfoQuery";
    import ListingProductInfoService from "../services/ListingProductInfoService";

    export let reviewsStorefrontValuesService: ReviewsStorefrontValuesService;
    export let listingProductInfoService: ListingProductInfoService;
    export let listingProductInfoQuery: ListingProductInfoQuery;
    export let productIdManager: ProductIdManagerInterface;
    export let showReviewsTooltip: boolean;
    export let afterWidgetInject: () => void;

    const reviewsBranding$ =
        reviewsStorefrontValuesService.selectReviewsBrandingOptions();

    const productId$ = productIdManager.productId$;

    const info$ = productId$.pipe(
        filter((productId): productId is number => productId !== null),
        switchMap((productId) =>
            listingProductInfoQuery.selectEntity(productId).pipe(
                map((info) => {
                    // If productId exists but data is missing, return empty object with zero values
                    if (!info) {
                        return {
                            productId,
                            avgRate: 0,
                            reviewCount: 0,
                            starsDistribution: [],
                        };
                    }
                    return info;
                })
            )
        )
    );
    const loading$ = listingProductInfoQuery.loading$;
    const error$ = listingProductInfoQuery.error$;

    onMount(() => {
        const productIdSubscription = productId$
            .pipe(
                filter((productId): productId is number => productId !== null),
                tap((productId) => {
                    listingProductInfoService.getBufferedListingProductInfo(
                        productId,
                        showReviewsTooltip ? true : undefined
                    );
                })
            )
            .subscribe();

        afterWidgetInject();

        return () => {
            productIdSubscription.unsubscribe();
        };
    });
</script>

{#if $reviewsBranding$}
    <ReviewsTheme branding={$reviewsBranding$}>
        <div
            class="gw-rv-listing-average-reviews-widget"
            data-gw-rating-count={$info$?.reviewCount}
            data-gw-rating-avg={$info$?.avgRate}
        >
            {#if !$error$}
                {#if $loading$}
                    <BaseSkeleton width="100px" height="20px" />
                    <BaseSkeleton width="30px" height="20px" />
                {:else if $info$}
                    {#if showReviewsTooltip}
                        <Tooltip
                            position="bottom"
                            maxWidth="240px"
                            mode="inherit"
                        >
                            <div
                                class="gw-rv-listing-average-reviews-widget__tooltip-stars"
                            >
                                <ReviewsRatingStars
                                    rating={$info$.avgRate || 0}
                                    starsSize={20}
                                    showIconTitle={false}
                                />
                                <Text variant="body-md">
                                    ({$info$.reviewCount})
                                </Text>
                            </div>

                            <slot slot="content">
                                <ReviewsTotalRate
                                    averageReviewsRating={$info$?.avgRate}
                                    ratesInfo={$info$?.starsDistribution}
                                    reviewsCount={$info$?.reviewCount}
                                />
                            </slot>
                        </Tooltip>
                    {:else}
                        <ReviewsRatingStars
                            rating={$info$.avgRate || 0}
                            starsSize={20}
                        />
                        <Text variant="body-md">
                            ({$info$.reviewCount})
                        </Text>
                    {/if}
                {/if}
            {/if}
        </div>
    </ReviewsTheme>
{/if}

<style lang="postcss" global>
    @import "../../../../../themes/default_v2/default.css";
    @import "../../../../../themes/globals.css";
    .gw-rv-listing-average-reviews-widget {
        position: relative;
        display: flex;
        align-items: flex-start;
        flex-wrap: wrap;
        gap: var(--gw-space-200);
        padding: var(--gw-space-0);

        &__tooltip-stars {
            display: flex;
            align-items: flex-start;
            gap: var(--gw-space-200);
            flex-wrap: wrap;
        }
    }
</style>
